import React, { useState, useEffect } from 'react';
import { Contact, Deal, Activity } from '../types';
import { crmDataService } from '../services/crmDataService'; // 🌟 API 서비스 추가
import { 
  FileText, TrendingUp, Calendar, Sparkles, 
  Printer, FileDown, User, PieChart
} from 'lucide-react';

interface ReportsProps {
  contacts: Contact[];
  deals: Deal[];
  activities: Activity[];
  currentUser?: { name: string; role: string };
  globalRefreshKey?: number;
}

type ReportType = 'daily' | 'weekly' | 'monthly';

const Reports: React.FC<ReportsProps> = ({ contacts, deals, activities, currentUser, globalRefreshKey = 0 }) => {
  const [selectedType, setSelectedType] = useState<ReportType>('weekly');
  const [reportContent, setReportContent] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  
  // 🌟 날짜 상태 관리
  const [targetDate, setTargetDate] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // 🌟 리포트 타입별 자동 날짜 셋팅 (대표님 원본 로직)
  useEffect(() => {
    const today = new Date();
    const todayStr = today.toISOString().split('T')[0];
    
    if (selectedType === 'daily') {
        setTargetDate(todayStr); 
    } else if (selectedType === 'weekly') {
        const lastWeek = new Date(today);
        lastWeek.setDate(today.getDate() - 7);
        setStartDate(lastWeek.toISOString().split('T')[0]); 
        setEndDate(todayStr);
    } else if (selectedType === 'monthly') {
        const lastMonth = new Date(today);
        lastMonth.setMonth(today.getMonth() - 1);
        setStartDate(lastMonth.toISOString().split('T')[0]); 
        setEndDate(todayStr);
    }
    setReportContent(''); 
  }, [selectedType]);

  // 🌟 담당자 추출: 모든 키값(한글/영어) 호환되도록 보정
  const uniqueAssignees = Array.from(new Set([
      ...deals.map((deal: any) => deal['담당자'] || deal.managerName || deal.assignee), 
      ...activities.map((act: any) => act.last_editor || act.editor || act.createdBy)
  ].filter(Boolean)));

  const isAdmin = true; 
  const [selectedAssignee, setSelectedAssignee] = useState<string>(isAdmin ? 'all' : (currentUser?.name || '담당자'));

  const reportTypes = [
    {
      id: 'daily',
      title: '일일 업무 보고서',
      desc: '선택한 하루 동안의 영업 활동 및 CS 방문 내역을 요약합니다.',
      icon: Calendar,
      color: 'bg-slate-700'
    },
    {
      id: 'weekly',
      title: '주간 업무 보고서 (IEG 양식)',
      desc: '매출 변동 이력과 미방문 거래처(S~D등급) 현황을 분석합니다.',
      icon: TrendingUp,
      color: 'bg-blue-600'
    },
    {
      id: 'monthly',
      title: '월간 통합 보고서',
      desc: '한 달간의 누적 매출 실적 및 수익률을 종합 점검합니다.',
      icon: PieChart,
      color: 'bg-indigo-600'
    }
  ];

  // 🚨 [진짜 API 호출부]
  const handleGenerate = async () => {
    setIsGenerating(true);
    setReportContent('');

    try {
      const finalStartDate = selectedType === 'daily' ? targetDate : startDate;
      const finalEndDate = selectedType === 'daily' ? targetDate : endDate;

      // 📡 파이썬 백엔드로 전송
      const result = await crmDataService.generateReport(
          selectedType, 
          finalStartDate, 
          finalEndDate, 
          selectedAssignee
      );

      if (result && result.status === 'success' && result.report_content) {
          setReportContent(result.report_content);
      } else {
          setReportContent("해당 기간의 데이터를 분석할 수 없거나 작성된 활동이 없습니다.");
      }
      setIsGenerating(false);
    } catch (e) {
      console.error("❌ 보고서 생성 실패:", e);
      setReportContent("서버와 통신하는 중 오류가 발생했습니다.");
      setIsGenerating(false);
    }
  };

  const handlePrintPDF = () => { window.print(); };

  return (
    <div className="min-h-screen flex flex-col animate-fade-in bg-[#f8fafc] pb-20 no-print">
      <div className="flex justify-between items-center mb-6">
        <div>
            <h2 className="text-2xl font-bold text-slate-800">지능형 영업 보고서</h2>
            <p className="text-sm text-slate-500">매출 변동 내역과 활동 로그를 분석하여 IEG 맞춤형 리포트를 생성합니다.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
            
            {/* 1. 리포트 기간 및 유형 */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                <h3 className="text-xs font-black text-slate-400 uppercase mb-4 tracking-tighter">1. 리포트 기간 및 유형</h3>
                
                <div className="mb-4 bg-slate-50 p-3 rounded-lg border border-slate-100">
                    {selectedType === 'daily' ? (
                        <div>
                            <label className="block text-[10px] font-bold text-slate-500 mb-1">기준일 (하루)</label>
                            <input type="date" value={targetDate} onChange={e => setTargetDate(e.target.value)} className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500" />
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 mb-1">시작일</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold text-slate-500 mb-1">종료일</label>
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-3">
                    {reportTypes.map((type) => {
                        const Icon = type.icon;
                        const isSelected = selectedType === type.id;
                        return (
                            <button
                                key={type.id}
                                onClick={() => setSelectedType(type.id as ReportType)}
                                className={`w-full text-left p-3 rounded-xl border transition-all relative ${
                                    isSelected ? 'border-blue-500 bg-blue-50 shadow-sm ring-1 ring-blue-500' : 'border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-lg text-white ${type.color}`}><Icon size={16} /></div>
                                    <div>
                                        <h4 className={`text-sm font-bold ${isSelected ? 'text-blue-900' : 'text-slate-800'}`}>{type.title}</h4>
                                        <p className="text-[10px] text-slate-500 mt-1 leading-tight">{type.desc}</p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* 2. 대상자 선택 */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                 <h3 className="text-xs font-black text-slate-400 uppercase mb-4 tracking-tighter flex items-center gap-1">
                    <User size={14}/> 2. 대상자 선택
                 </h3>
                 <div className="space-y-2">
                    {isAdmin ? (
                        <select 
                            value={selectedAssignee} 
                            onChange={e => setSelectedAssignee(e.target.value)} 
                            className="w-full p-2.5 border border-slate-200 rounded-lg text-xs font-bold focus:border-blue-500 outline-none bg-white cursor-pointer"
                        >
                            <option value="all">전체 팀 통합 보고서 (Master)</option>
                            {uniqueAssignees.map(name => (
                                <option key={name} value={name}>{name}</option>
                            ))}
                        </select>
                    ) : (
                        <div className="w-full p-2.5 border border-slate-100 bg-slate-50 rounded-lg text-xs font-bold text-slate-500 flex items-center justify-between cursor-not-allowed">
                            <span>{selectedAssignee}</span>
                            <span className="text-[9px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">본인 전용</span>
                        </div>
                    )}
                 </div>
            </div>

            <div className="pt-2">
                <button
                    onClick={handleGenerate}
                    disabled={isGenerating}
                    className="w-full bg-slate-900 text-white py-3.5 rounded-xl font-bold hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 shadow-md"
                >
                    {isGenerating ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : <Sparkles size={18} />}
                    {isGenerating ? '데이터 수집 및 분석 중...' : '보고서 자동 작성'}
                </button>
            </div>
        </div>

        {/* 오른쪽: 미리보기 영역 */}
        <div className="lg:col-span-2 flex flex-col min-h-[600px]">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 flex-1 flex flex-col overflow-hidden">
                <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
                    <div className="flex items-center gap-2 text-slate-700 font-bold">
                        <FileText size={18} />
                        <span>리포트 미리보기</span>
                    </div>
                    {reportContent && (
                        <div className="flex gap-2">
                             <button onClick={handlePrintPDF} className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-slate-50 transition-colors">
                                <Printer size={14} /> PDF 인쇄
                             </button>
                        </div>
                    )}
                </div>
                
                <div className="flex-1 p-8 overflow-y-auto bg-white" id="printable-report">
                    {isGenerating ? (
                        <div className="h-full flex flex-col items-center justify-center space-y-4">
                            <div className="w-12 h-12 border-4 border-blue-100 border-t-blue-600 rounded-full animate-spin"></div>
                            <p className="text-slate-500 font-medium animate-pulse">DB 데이터 추출 및 AI 분석 중...</p>
                        </div>
                    ) : reportContent ? (
                        <div className="prose prose-slate max-w-none">
                             <div className="whitespace-pre-wrap font-sans text-slate-700 leading-relaxed text-sm">
                                {reportContent}
                             </div>
                        </div>
                    ) : (
                        <div className="h-full flex flex-col items-center justify-center text-slate-300">
                            <FileDown size={48} className="mb-4 opacity-20" />
                            <p className="font-medium text-slate-400">조건을 설정하고 생성 버튼을 누르세요.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
      </div>
      <style>{`@media print { .no-print { display: none !important; } #printable-report { position: absolute; left: 0; top: 0; width: 100%; padding: 0; } body { background: white; } }`}</style>
    </div>
  );
};

export default Reports;